import {Icon} from '@/components/atoms';
import {COLOR_CODE} from '@/utils/constants/colors';
import './_labelWithoutJson.scss';
import {FONT_SIZE, FONT_WEIGHT} from '@/utils/constants/globalStyle';
import PropTypes from 'prop-types';

/**
 * LabelWithoutJson component renders a text label with optional icon.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.id - The unique identifier for the label.
 * @param {string} props.text - The text content of the label.
 * @param {string} [props.fontSize='text-sm'] - The font size of the label text.
 * @param {string} [props.fontWeight='font-normal'] - The font weight of the label text.
 * @param {string} [props.fontColor=COLOR_CODE._D0D0D0] - The font color of the label text.
 * @param {string} [props.textAlign='text-left'] - The text alignment of the label text.
 * @param {boolean} [props.isIconVisible=false] - Flag to determine if the icon should be visible.
 * @param {string} [props.iconName='info'] - The name of the icon to display.
 * @param {string} [props.iconSize='small'] - The size of the icon to display.
 * @returns {JSX.Element} The rendered LabelWithoutJson component.
 */
export default function LabelWithoutJson({
  id,
  text,
  fontSize = FONT_SIZE.SM,
  fontWeight = FONT_WEIGHT.NORMAL,
  color = COLOR_CODE._D0D0D0,
  textAlign = 'left',
  isIconVisible = false,
  iconName = 'info',
  iconSize = 'small',
  isValueDiasbledInput = false
}) {
  const containerClassNames = ['flex', 'items-center', 'gap-2'];
  if (isValueDiasbledInput) {
    containerClassNames.push('lwj-input', 'w-full', 'pl-2');
  }
  const idPrefix = `label_${id.replace(/ /g, '_').toLowerCase()}`;

  return (
    <div className={containerClassNames.join(' ')}>
      <div
        id={idPrefix}
        name={idPrefix}
        className='label-block'
        style={{
          '--dynamic-color': color,
          '--dynamic-font-size': fontSize,
          '--dynamic-font-weight': fontWeight,
          dynamicTextAlign: textAlign
        }}>
        {text}
      </div>
      {isIconVisible && <Icon icon={iconName} size={iconSize} />}
    </div>
  );
}

LabelWithoutJson.propTypes = {
  id: PropTypes.string,
  text: PropTypes.string.isRequired,
  fontSize: PropTypes.string,
  fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
  textAlign: PropTypes.string,
  isIconVisible: PropTypes.bool,
  iconName: PropTypes.string,
  iconSize: PropTypes.string,
  isValueDiasbledInput: PropTypes.bool
};
